/**
 * Formik-bound QueryBuilder wrapper for notification rules.
 * Sibling of `GuardrailConditionsQueryBuilderFormik` — same skeleton (read
 * `values[fieldId]`, gate on `submitCount`, compute prereqs, call the shared
 * registry hook, fire `onFieldsLoaded`, render the pure QB) parameterised over
 * the `notification_rule` rule_set_type.
 *
 * Like the guardrails sibling, the catalog fetch passes the rule's event through
 * and the BE narrows the field registry by event scope — so a claim-scoped event
 * offers no `exposure.*` / `involved_party.*` fields, and an `event.*` namespace
 * is offered at all only once an event is picked. That is also the catalog the
 * save path validates against: the companion `AutomationRule` carries the rule's
 * own `event_key`, so what the builder offers and what the save accepts are one
 * catalog rather than two that can disagree. Prereq gate is therefore event +
 * LOB + sub-org, matching the guardrails wrapper. Changing the event resets
 * `conditions_json` in the parent dialog, so a narrowed catalog can never be
 * asked to validate a tree authored under a wider one.
 *
 * The one difference from the guardrails sibling: org context comes from
 * `useSysconfig` (the source the notifications screen already reads) rather than
 * `useOrganization`, so this gate and the parent dialog's `isAllSubOrgs` /
 * `subOrgIds` derivation cannot disagree about whether sub-orgs are enabled.
 *
 * The composed pure `RuleConditionsQueryBuilder` writes its tree into Formik
 * under `fieldId` (default `'conditions_json'`).
 */

import React, { useEffect, useMemo } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';

import RuleConditionsQueryBuilder from '../../../Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '../../../Conditions/QueryBuilder/types';
import { useRuleSetConditionFieldRegistry } from '../../../Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

const NOTIFICATION_RULE_TYPE = 'notification_rule';

interface NotificationConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** Event firing the rule. Empty string = "not yet picked" — the catalog fetch
   *  is gated until the user selects an event (the BE needs it to narrow the
   *  catalog by event scope and to serve the `event.*` namespace). */
  eventKey: string;
  /** Whether the rule fires with the event or on a custom schedule. A custom-schedule
   *  rule is evaluated by the cron long after the event, from the persisted task alone,
   *  so the event payload is gone and every `event.*` condition would answer False for
   *  every record — the save path refuses them with a 400, and this drops them from the
   *  builder so nobody can pick one. */
  isImmediate: boolean;
  lob: string;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Surfaces the catalog-fetch loading state so the parent dialog can disable
   *  Save while fields are still in flight. When prereqs aren't met the fetcher
   *  is idle (isLoading=false), so Save behaves normally outside of fetches. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
}

interface PrerequisiteCheck {
  /** True when LOB + (sub-org-set OR all-sub-orgs OR sub-orgs-disabled) both hold. */
  prerequisitesMet: boolean;
  /** Copy shown in place of the QB while prerequisites aren't met; `null` when met. */
  prerequisiteMessage: string | null;
}

/** Compute the notification-specific prerequisite predicate + placeholder copy.
 *  Every dimension the catalog fetch scopes on is checked: the BE catalog
 *  endpoint requires `rule_set_type`, `event_key` and `lob`, and sub-org-enabled
 *  orgs also need an explicit sub-org pick (or the all-sub-orgs flag). */
const checkPrerequisites = (
  eventKey: string,
  lob: string,
  isAllSubOrgs: boolean,
  subOrgIds: number[],
  subOrgsEnabled: boolean
): PrerequisiteCheck => {
  const hasOrgScope = !subOrgsEnabled || isAllSubOrgs || (subOrgIds && subOrgIds.length > 0);
  if (eventKey && lob && hasOrgScope) {
    return { prerequisitesMet: true, prerequisiteMessage: null };
  }
  const subOrgPart = subOrgsEnabled ? ', and Sub-Organization' : '';
  return {
    prerequisitesMet: false,
    prerequisiteMessage: `Select an Event and a Line of Business${subOrgPart} to configure conditions.`,
  };
};

const NotificationConditionsQueryBuilderFormik: React.FC<NotificationConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  eventKey,
  isImmediate,
  lob,
  isAllSubOrgs,
  subOrgIds,
  onLoadingChange,
  onFieldsLoaded,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // `useSysconfig` is exported from a JSX file without types; cast at the boundary.
  const { organization } = useSysconfig() as {
    organization: { id: number; sub_organizations_enabled?: boolean };
  };

  const { prerequisitesMet, prerequisiteMessage } = checkPrerequisites(
    eventKey,
    lob,
    isAllSubOrgs,
    subOrgIds,
    !!organization.sub_organizations_enabled
  );

  const { fields: catalogFields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId: organization.id,
    enabled: prerequisitesMet,
    ruleSetType: NOTIFICATION_RULE_TYPE,
    eventKey,
    // Single-LOB consumer: the rule carries one LOB, so the list-shaped catalog
    // scope is that one LOB. `enabled` already gates on it being picked.
    lobs: [lob],
    isAllSubOrgs,
    subOrgIds,
  });

  // Filtered here rather than at the catalog endpoint: `is_immediate` is a property of the
  // rule being authored, not of the rule set, the event or the scope the endpoint is keyed on
  // — and the form already knows it. `fields` is what both the builder and the parent's
  // form-level `validate` see, so the two agree on what may be picked.
  const fields = useMemo(
    () => (isImmediate ? catalogFields : catalogFields.filter((field) => !field.path.startsWith('event.'))),
    [catalogFields, isImmediate]
  );

  // Depend on the boolean only, not the callback identity (parent wires a stable
  // useState setter). Including the callback could infinite-loop on inline arrows.
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Same callback-identity caveat as onLoadingChange: depend on `fields` only.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this event and LOB."
      // The builder renders only once an event is picked (see the prerequisite gate), so every
      // rule authored here has one.
      hasTriggeringEvent
    />
  );
};

export default NotificationConditionsQueryBuilderFormik;
